import { useState, useEffect, useCallback } from "react";
import {
  generateTimeCardReport,
  updateTimecard,
  deleteTimecard,
  restoreTimecard,
  getDeletedTimecards,
} from "../Api/TimeCardReport";
import { getResourcesByOrgId } from "../Api/ResourceList";
import {
  fetchProjectBaseByOrg,
  fetchProjectBaseByOrgAndMember,
} from "../Api/ViewProjects";
import {
  getTasksByProject,
  getTasksByAssignedTo,
  getAllTaskStatus,
  getContactGuid,
} from "../Api/TaskMaster";
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Table,
  Modal,
} from "react-bootstrap";
import { toast } from "react-toastify";
import Sample14 from "../Components/Logo";
import SearchableSelect from "../Pages/ProjectComponents/SearchableSelect";
import SearchableMultiSelect from "../Pages/ProjectComponents/SearchableMultiSelect";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./TimeCardReport.css";

const TimeCardReport = () => {
  const [loading, setLoading] = useState(false);
  const [fetchingResources, setFetchingResources] = useState(false);
  const [fetchingProjects, setFetchingProjects] = useState(false);
  const [fetchingTasks, setFetchingTasks] = useState(false);
  const [resources, setResources] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [userContactGuid, setUserContactGuid] = useState(null);
  const [taskStatusMap, setTaskStatusMap] = useState({});
  const [reportData, setReportData] = useState({
    ResourceCodes: [],
    ProjectCode: "",
    TaskId: "",
    FromDate: "",
    ToDate: "",
  });
  const [reportResults, setReportResults] = useState({
    data: [],
    message: "",
    count: 0,
    resourceIds: [],
    fromDate: "",
    toDate: "",
  });
  const [showResults, setShowResults] = useState(false);
  const [errors, setErrors] = useState({});

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTimecard, setSelectedTimecard] = useState(null);
  const [editFormData, setEditFormData] = useState({
    date: "",
    projectCode: "",
    taskId: "",
    tasks: "",
    hours: "",
    remarks: "",
    location: "",
    status: "",
    resourceId: "",
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [timecardToDelete, setTimecardToDelete] = useState(null);
  const [showDeletedModal, setShowDeletedModal] = useState(false);
  const [deletedTimecards, setDeletedTimecards] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [editProjects, setEditProjects] = useState([]);
  const [editTasks, setEditTasks] = useState([]);
  const [fetchingEditProjects, setFetchingEditProjects] = useState(false);
  const [fetchingEditTasks, setFetchingEditTasks] = useState(false);
const [activeSummaryTab, setActiveSummaryTab] = useState("resource");
  // Define admin roles
  const ADMIN_ROLES = ["Admin"];

  // Check if user has admin role
  const hasAdminRole = (roles) => {
    if (!roles || !Array.isArray(roles)) return false;
    return roles.some((role) => ADMIN_ROLES.includes(role));
  };

  // Check if user has view permission for all tasks
  const hasViewPermission = (roles) => {
    if (!roles || !Array.isArray(roles)) return false;
    return roles.some((role) =>
      ["Admin", "Project_Manager", "Project_Commercial"].includes(role),
    );
  };

  // Get current user from localStorage
  const getCurrentUser = () => {
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    return {
      userId: userDetails?.userId || localStorage.getItem("memberId"),
      roles: userDetails?.roles || [],
      username: userDetails?.username || "",
    };
  };

  const currentUser = getCurrentUser();
  const isAdmin = hasAdminRole(currentUser.roles);

  // Calculate total hours from report data
  const calculateTotalHours = () => {
    if (!reportResults.data || reportResults.data.length === 0) return 0;
    return reportResults.data.reduce((total, item) => {
      const hours = parseFloat(item.Hours) || 0;
      return total + hours;
    }, 0);
  };

  // Calculate hours by resource for summary
  const calculateHoursByResource = () => {
    if (!reportResults.data || reportResults.data.length === 0) return [];

    const resourceMap = new Map();
    reportResults.data.forEach((item) => {
      const resourceId = item.ResourceId || item.ResourceCode;
      const userName = item.UserName;
      const hours = parseFloat(item.Hours) || 0;

      if (!resourceMap.has(resourceId)) {
        resourceMap.set(resourceId, {
          resourceId: resourceId,
          userName: userName,
          hours: 0,
          recordCount: 0,
        });
      }

      const resource = resourceMap.get(resourceId);
      resource.hours += hours;
      resource.recordCount += 1;
    });

    return Array.from(resourceMap.values()).sort((a, b) => b.hours - a.hours);
  };

  // Calculate hours by project for summary
  const calculateHoursByProject = () => {
    if (!reportResults.data || reportResults.data.length === 0) return [];

    const projectMap = new Map();
    reportResults.data.forEach((item) => {
      const projectCode = item.ProjectCode;
      const projectName = item.ProjectName;
      const hours = parseFloat(item.Hours) || 0;

      if (!projectMap.has(projectCode)) {
        projectMap.set(projectCode, {
          projectCode: projectCode,
          projectName: projectName || "N/A",
          hours: 0,
          recordCount: 0,
        });
      }

      const project = projectMap.get(projectCode);
      project.hours += hours;
      project.recordCount += 1;
    });

    return Array.from(projectMap.values()).sort((a, b) => b.hours - a.hours);
  };

  // Calculate hours by status for summary
  const calculateHoursByStatus = () => {
    if (!reportResults.data || reportResults.data.length === 0) return [];

    const statusMap = new Map();
    reportResults.data.forEach((item) => {
      const status =
        item.StatusName ||
        (item.Status === "W"
          ? "Pending"
          : item.Status === "A"
            ? "Approved"
            : item.Status === "R"
              ? "Rejected"
              : item.Status || "Unknown");
      const hours = parseFloat(item.Hours) || 0;

      if (!statusMap.has(status)) {
        statusMap.set(status, {
          status: status,
          hours: 0,
          recordCount: 0,
        });
      }

      const statusItem = statusMap.get(status);
      statusItem.hours += hours;
      statusItem.recordCount += 1;
    });

    return Array.from(statusMap.values()).sort((a, b) => b.hours - a.hours);
  };

  // Load all task statuses for display
  const loadAllTaskStatuses = async () => {
    try {
      const statuses = await getAllTaskStatus();
      const statusMap = {};
      statuses.forEach((status) => {
        statusMap[status.statusGuid.toLowerCase()] = status.statusName;
      });
      setTaskStatusMap(statusMap);
    } catch (error) {
      console.error("Error loading task statuses:", error);
    }
  };

  // Get task status display name
  const getTaskStatusName = (statusGuid) => {
    if (!statusGuid) return "No Status";
    const lowerGuid = statusGuid.toLowerCase();
    return taskStatusMap[lowerGuid] || statusGuid;
  };

  // Fetch user's contact GUID
  const fetchUserContactGuid = async (orgId, userId) => {
    try {
      const response = await getContactGuid(orgId, userId);
      const contactGuid = response.contactGuid;
      setUserContactGuid(contactGuid);
      return contactGuid;
    } catch (error) {
      console.error("Error fetching user contact GUID:", error);
      return null;
    }
  };

  // Load projects for edit modal
  const loadEditProjects = async () => {
    setFetchingEditProjects(true);
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const orgId =
        userDetails?.organisationId || localStorage.getItem("orgId");
      const currentUserId =
        userDetails?.userId || localStorage.getItem("memberId");
      const userRoles = userDetails?.roles || [];
      const admin = hasAdminRole(userRoles);

      let projectsData;
      if (admin) {
        projectsData = await fetchProjectBaseByOrg(orgId);
      } else {
        projectsData = await fetchProjectBaseByOrgAndMember(
          orgId,
          currentUserId,
        );
      }

      const mappedProjects = (projectsData || [])
        .filter((project) => project.active === 1)
        .map((project) => ({
          projectId: project.project_id || project.projectId,
          prTitle: project.pr_Title || project.prTitle,
        }));

      setEditProjects(mappedProjects);
    } catch (error) {
      console.error("Error loading projects for edit:", error);
      toast.error("Failed to load projects");
    } finally {
      setFetchingEditProjects(false);
    }
  };

  // Load tasks for edit modal based on selected project
  const loadEditTasks = async (projectId) => {
    if (!projectId) {
      setEditTasks([]);
      return;
    }

    setFetchingEditTasks(true);
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const userRoles = userDetails?.roles || [];
      let tasksData = [];
      const admin = hasViewPermission(userRoles);

      if (admin) {
        tasksData = await getTasksByProject(projectId);
      } else {
        if (!userContactGuid) {
          setEditTasks([]);
          return;
        }
        const assignedTasks = await getTasksByAssignedTo(userContactGuid);
        tasksData = assignedTasks.filter(
          (task) =>
            task.projectBelong === projectId ||
            task.projectBelong === parseInt(projectId),
        );
      }

      const mappedTasks = tasksData.map((task) => ({
        taskId: task.taskId,
        taskName: task.taskName,
        displayValue: `${task.taskId} - ${task.taskName}`,
      }));

      setEditTasks(mappedTasks);
    } catch (error) {
      console.error("Error loading tasks for edit:", error);
      setEditTasks([]);
    } finally {
      setFetchingEditTasks(false);
    }
  };

  // Load projects based on user role
  const loadProjects = async (orgId, userId, userRoles) => {
    setFetchingProjects(true);
    try {
      let projectsData;
      const admin = hasAdminRole(userRoles);

      if (admin) {
        projectsData = await fetchProjectBaseByOrg(orgId);
      } else {
        projectsData = await fetchProjectBaseByOrgAndMember(orgId, userId);
      }

      const mappedProjects = (projectsData || [])
        .filter((project) => project.active === 1)
        .map((project) => ({
          projectId: project.project_id || project.projectId,
          prTitle: project.pr_Title || project.prTitle,
          ...project,
        }));

      setProjects(mappedProjects);
      return mappedProjects;
    } catch (error) {
      console.error("Error loading projects:", error);
      toast.error("Failed to load projects");
      return [];
    } finally {
      setFetchingProjects(false);
    }
  };

  // Load tasks based on selected project
  const loadTasks = useCallback(async (projectId, userRoles, contactGuid) => {
    if (!projectId) {
      setTasks([]);
      return;
    }

    setFetchingTasks(true);
    try {
      let tasksData = [];
      const admin = hasViewPermission(userRoles);

      if (admin) {
        tasksData = await getTasksByProject(projectId);
      } else {
        if (!contactGuid) {
          setTasks([]);
          return;
        }
        const assignedTasks = await getTasksByAssignedTo(contactGuid);
        tasksData = assignedTasks.filter(
          (task) =>
            task.projectBelong === projectId ||
            task.projectBelong === parseInt(projectId),
        );
      }

      const mappedTasks = tasksData.map((task) => ({
        taskId: task.taskId,
        taskName: task.taskName,
        taskStatus: getTaskStatusName(task.taskStatus),
        displayValue: `${task.taskId} - ${task.taskName}`,
      }));

      setTasks(mappedTasks);
    } catch (error) {
      console.error("Error loading tasks:", error);
      setTasks([]);
    } finally {
      setFetchingTasks(false);
    }
  }, []);

  // Excel Download Function
  const downloadExcel = () => {
    if (!reportResults.data.length) {
      toast.warning("No data to export");
      return;
    }

    try {
      const totalHours = calculateTotalHours();
      const hoursByResource = calculateHoursByResource();
      const hoursByProject = calculateHoursByProject();

      const excelData = reportResults.data.map((item) => ({
        "DT Id": item.DTId || "",
        "Resource ID": item.ResourceId || item.ResourceCode || "",
        "User Name": item.UserName || "",
        Date: item.Date || "",
        "Project Code": item.ProjectCode || "",
        "Project Name": item.ProjectName || "N/A",
        "Task ID": item.TaskId || "",
        "Task Name": item.TaskName || "N/A",
        Hours: item.Hours || "",
        Remarks: item.Remarks || "",
        Location: item.Location || "",
        Status:
          item.StatusName ||
          (item.Status === "W"
            ? "Pending"
            : item.Status === "A"
              ? "Approved"
              : item.Status === "R"
                ? "Rejected"
                : item.Status),
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Add summary rows to the worksheet
      const summaryRows = [
        [],
        ["SUMMARY REPORT"],
        [],
        [`TOTAL HOURS:`, totalHours.toFixed(2)],
        [],
        ["HOURS BY RESOURCE:"],
        ["Resource ID", "User Name", "Hours", "Record Count"],
        ...hoursByResource.map((r) => [
          r.resourceId,
          r.userName,
          r.hours.toFixed(2),
          r.recordCount,
        ]),
        [],
        ["HOURS BY PROJECT:"],
        ["Project Code", "Project Name", "Hours", "Record Count"],
        ...hoursByProject.map((p) => [
          p.projectCode,
          p.projectName,
          p.hours.toFixed(2),
          p.recordCount,
        ]),
      ];

      // Append summary rows to worksheet
      const currentRowCount = excelData.length;
      summaryRows.forEach((row, index) => {
        const rowNum = currentRowCount + index + 1;
        row.forEach((cell, colNum) => {
          const cellAddress = XLSX.utils.encode_cell({ r: rowNum, c: colNum });
          worksheet[cellAddress] = { t: "s", v: cell };
        });
      });

      const colWidths = [
        { wch: 10 },
        { wch: 15 },
        { wch: 20 },
        { wch: 12 },
        { wch: 15 },
        { wch: 35 },
        { wch: 15 },
        { wch: 35 },
        { wch: 10 },
        { wch: 40 },
        { wch: 20 },
        { wch: 12 },
      ];
      worksheet["!cols"] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "TimeCardReport");

      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, 19);
      const filename = `TimeCardReport_${timestamp}.xlsx`;
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });
      saveAs(blob, filename);

      toast.success(
        `Excel file downloaded successfully! (${reportResults.data.length} records, Total Hours: ${totalHours.toFixed(2)})`,
      );
    } catch (error) {
      console.error("Error downloading Excel:", error);
      toast.error("Failed to download Excel file");
    }
  };

  // Edit Timecard Handlers
  const handleEditClick = async (timecard) => {
    // Load projects for edit modal
    await loadEditProjects();

    setSelectedTimecard(timecard);
    setEditFormData({
      date: timecard.Date || "",
      projectCode: timecard.ProjectCode || "",
      taskId: timecard.TaskId || "",
      tasks: timecard.TaskId || "", // Task ID as string
      hours: timecard.Hours || "",
      remarks: timecard.Remarks || "",
      location: timecard.Location || "",
      status: timecard.Status || "W",
      resourceId: timecard.ResourceId || "",
    });

    // Load tasks for the selected project
    if (timecard.ProjectCode) {
      await loadEditTasks(timecard.ProjectCode);
    }

    setShowEditModal(true);
  };

  const handleEditProjectChange = async (projectCode) => {
    setEditFormData((prev) => ({
      ...prev,
      projectCode: projectCode,
      taskId: "",
      tasks: "",
    }));
    if (projectCode) {
      await loadEditTasks(projectCode);
    } else {
      setEditTasks([]);
    }
  };

  const handleEditTaskChange = (taskId) => {
    setEditFormData((prev) => ({
      ...prev,
      taskId: taskId,
      tasks: taskId,
    }));
  };

  const handleUpdateTimecard = async () => {
    setUpdating(true);
    try {
      const updateData = {
        date: editFormData.date,
        projectCode: editFormData.projectCode,
        hours: parseFloat(editFormData.hours),
        tasks: editFormData.tasks,
        remarks: editFormData.remarks,
        location: editFormData.location,
        status: editFormData.status,
        resourceId: editFormData.resourceId,
      };

      const response = await updateTimecard(selectedTimecard.DTId, updateData);
      toast.success(response.message || "Timecard updated successfully!");

      // Refresh the report
      await refreshReport();
      setShowEditModal(false);
    } catch (error) {
      toast.error(error.message || "Error updating timecard");
    } finally {
      setUpdating(false);
    }
  };

  // Delete Timecard Handlers (Admin only)
  const handleDeleteClick = (timecard) => {
    if (!isAdmin) {
      toast.warning("Only administrators can delete timecards");
      return;
    }
    setTimecardToDelete(timecard);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setUpdating(true);
    try {
      const response = await deleteTimecard(timecardToDelete.DTId);
      toast.success(response.message || "Timecard deleted successfully!");

      // Refresh the report
      await refreshReport();
      setShowDeleteConfirm(false);
      setTimecardToDelete(null);
    } catch (error) {
      toast.error(error.message || "Error deleting timecard");
    } finally {
      setUpdating(false);
    }
  };

  // View Deleted Timecards (Admin only)
  const handleViewDeleted = async () => {
    if (!isAdmin) {
      toast.warning("Only administrators can view deleted timecards");
      return;
    }

    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const organisationId =
        userDetails?.organisationId || localStorage.getItem("orgId");
      const response = await getDeletedTimecards(organisationId);
      setDeletedTimecards(response.data);
      setShowDeletedModal(true);
      toast.success(response.message);
    } catch (error) {
      toast.error(error.message || "Error fetching deleted timecards");
    }
  };

  // Restore Timecard Handler (Admin only)
  const handleRestoreTimecard = async (dtid) => {
    if (!isAdmin) {
      toast.warning("Only administrators can restore timecards");
      return;
    }

    setUpdating(true);
    try {
      const response = await restoreTimecard(dtid);
      toast.success(response.message || "Timecard restored successfully!");

      // Refresh both reports
      await refreshReport();
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const organisationId =
        userDetails?.organisationId || localStorage.getItem("orgId");
      const deletedResponse = await getDeletedTimecards(organisationId);
      setDeletedTimecards(deletedResponse.data);
    } catch (error) {
      toast.error(error.message || "Error restoring timecard");
    } finally {
      setUpdating(false);
    }
  };

  // Refresh report after edit/delete
  const refreshReport = async () => {
    try {
      const response = await generateTimeCardReport({
        ResourceCodes: reportData.ResourceCodes,
        ProjectCode: reportData.ProjectCode,
        TaskId: reportData.TaskId,
        FromDate: reportData.FromDate,
        ToDate: reportData.ToDate,
      });
      setReportResults(response);
    } catch (error) {
      console.error("Error refreshing report:", error);
    }
  };

  // Fetch resources based on user role
  useEffect(() => {
    const loadInitialData = async () => {
      setFetchingResources(true);
      try {
        const userDetails = JSON.parse(localStorage.getItem("userDetails"));
        const userRoles = userDetails?.roles || [];
        const orgId =
          userDetails?.organisationId || localStorage.getItem("orgId");
        const currentUserId =
          userDetails?.userId || localStorage.getItem("memberId");

        await loadAllTaskStatuses();
        await fetchUserContactGuid(orgId, currentUserId);
        await loadProjects(orgId, currentUserId, userRoles);

        const allResources = await getResourcesByOrgId(orgId);
        const activeResources = allResources.filter(
          (resource) => resource.active === true,
        );

        const admin = hasAdminRole(userRoles);

        if (admin) {
          const formattedResources = activeResources.map((resource) => ({
            id: resource.resourceId,
            name: `${resource.resourceId} - ${resource.name} (${resource.emailId})`,
          }));
          setResources(formattedResources);
        } else {
          const currentUserResource = activeResources.find(
            (resource) => resource.mavUserId === currentUserId,
          );

          if (currentUserResource) {
            const formattedResource = {
              id: currentUserResource.resourceId,
              name: `${currentUserResource.resourceId} - ${currentUserResource.name}`,
            };
            setResources([formattedResource]);
            setReportData((prev) => ({
              ...prev,
              ResourceCodes: [currentUserResource.resourceId],
            }));
          } else {
            toast.warning("No resource profile found for your account");
            setResources([]);
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error(`Error loading data: ${error.message || "Unknown error"}`);
      } finally {
        setFetchingResources(false);
      }
    };

    loadInitialData();
  }, []);

  // Load tasks when project changes
  useEffect(() => {
    const loadTasksAsync = async () => {
      if (reportData.ProjectCode) {
        const userDetails = JSON.parse(localStorage.getItem("userDetails"));
        const userRoles = userDetails?.roles || [];

        await loadTasks(reportData.ProjectCode, userRoles, userContactGuid);

        setReportData((prev) => ({
          ...prev,
          TaskId: "",
        }));
      } else {
        setTasks([]);
      }
    };

    loadTasksAsync();
  }, [reportData.ProjectCode, loadTasks, userContactGuid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReportData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }

    if (name === "FromDate" && !value) {
      setReportData((prev) => ({ ...prev, ToDate: "" }));
      setErrors((prev) => ({ ...prev, ToDate: null }));
    }
  };

  const handleResourceChange = (selectedResources) => {
    setReportData((prev) => ({
      ...prev,
      ResourceCodes: selectedResources,
    }));
  };

  const handleProjectChange = (projectCode) => {
    setReportData((prev) => ({
      ...prev,
      ProjectCode: projectCode,
      TaskId: "",
    }));
  };

  const handleTaskChange = (taskId) => {
    setReportData((prev) => ({
      ...prev,
      TaskId: taskId,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const admin = hasAdminRole(userDetails?.roles || []);

    if (!admin && reportData.ResourceCodes.length === 0) {
      newErrors.ResourceCodes = "Resource Code is required";
    }

    if (reportData.FromDate && !reportData.ToDate) {
      newErrors.ToDate = "To Date is required when From Date is entered";
    }

    if (reportData.FromDate && reportData.ToDate) {
      if (new Date(reportData.ToDate) < new Date(reportData.FromDate)) {
        newErrors.ToDate = "To Date must be after From Date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await generateTimeCardReport({
        ResourceCodes: reportData.ResourceCodes,
        ProjectCode: reportData.ProjectCode,
        TaskId: reportData.TaskId,
        FromDate: reportData.FromDate,
        ToDate: reportData.ToDate,
      });
      console.log(response);
      setReportResults(response);
      setShowResults(true);

      if (reportData.ResourceCodes.length === 0) {
        toast.success(
          `✅ Report Generated for All Resources (${response.count} records)`,
        );
      } else if (reportData.ResourceCodes.length === 1) {
        toast.success(
          `✅ ${response.message || "Report Generated Successfully"}`,
        );
      } else {
        toast.success(
          `✅ Report Generated for ${reportData.ResourceCodes.length} Resources (${response.count} records)`,
        );
      }
    } catch (error) {
      toast.error(`❌ ${error.message || "Error Generating Report"}`);
      setShowResults(false);
    } finally {
      setLoading(false);
    }
  };

  const projectOptions = projects.map((project) => ({
    id: project.projectId,
    name: `${project.projectId} - ${project.prTitle}`,
  }));

  const taskOptions = tasks.map((task) => ({
    id: task.taskId,
    name: task.displayValue,
  }));

  const editProjectOptions = editProjects.map((project) => ({
    id: project.projectId,
    name: `${project.projectId} - ${project.prTitle}`,
  }));

  const editTaskOptions = editTasks.map((task) => ({
    id: task.taskId,
    name: task.displayValue,
  }));

  const totalHours = calculateTotalHours();
  const hoursByResource = calculateHoursByResource();
  const hoursByProject = calculateHoursByProject();
  const hoursByStatus = calculateHoursByStatus();

  return (
    <div className="tcr-main-container">
      <div className="tcr-fields-container">
        <h2 className="tcr-title">Time Card Report</h2>

        <div className="tcr-alert-info">
          <i className="fas fa-info-circle"></i>
          {isAdmin ? (
            <span>
              <strong>Admin Access:</strong> You can filter time cards by
              multiple Resources, Project, and Task. Leave Resources empty to
              view all resources, or select specific ones for focused reports.
              <br />
              <strong>Delete Access:</strong> As admin, you can delete and
              restore timecards.
            </span>
          ) : (
            <span>
              <strong>Limited Access:</strong> You can only view your own time
              cards.
              <br />
              <strong>Edit Access:</strong> You can edit your own timecards.
            </span>
          )}
        </div>

        <Form onSubmit={handleSubmit} className="tcr-fields-form">
          <Row className="tcr-row">
            <Col md={4} className="tcr-col">
              <Form.Group className="tcr-form-group">
                <Form.Label className="tcr-form-label">
                  Resources{" "}
                  {!isAdmin && <span className="tcr-text-danger">**</span>}
                </Form.Label>
                {isAdmin ? (
                  <SearchableMultiSelect
                    value={reportData.ResourceCodes}
                    onChange={handleResourceChange}
                    options={resources}
                    placeholder="Select Resources (Leave empty for All Resources)"
                    disabled={fetchingResources}
                    loading={fetchingResources}
                    optionLabelKey="name"
                    optionValueKey="id"
                    searchPlaceholder="Search resources..."
                    noResultsText="No resources found"
                  />
                ) : (
                  <Form.Select
                    name="ResourceCode"
                    value={reportData.ResourceCodes[0] || ""}
                    onChange={(e) => handleResourceChange([e.target.value])}
                    required={!isAdmin}
                    disabled={fetchingResources || resources.length === 1}
                    isInvalid={!!errors.ResourceCodes}
                    className="tcr-form-select"
                  >
                    {resources.map((resource) => (
                      <option key={resource.id} value={resource.id}>
                        {resource.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
                {errors.ResourceCodes && (
                  <Form.Text className="text-danger">
                    {errors.ResourceCodes}
                  </Form.Text>
                )}
                {isAdmin && !fetchingResources && (
                  <small className="tcr-text-muted tcr-text-info">
                    <i className="fas fa-info-circle tcr-info-icon"></i>
                    Select specific resources or leave empty to view all
                    resources
                  </small>
                )}
              </Form.Group>
            </Col>

            <Col md={4} className="tcr-col">
              <Form.Group className="tcr-form-group">
                <Form.Label className="tcr-form-label">
                  Project (Optional)
                </Form.Label>
                <SearchableSelect
                  value={reportData.ProjectCode}
                  onChange={handleProjectChange}
                  options={projectOptions}
                  placeholder="Select Project"
                  disabled={fetchingProjects || projects.length === 0}
                  loading={fetchingProjects}
                  optionLabelKey="name"
                  optionValueKey="id"
                  searchPlaceholder="Search project..."
                  noResultsText="No projects found"
                />
              </Form.Group>
            </Col>

            <Col md={4} className="tcr-col">
              <Form.Group className="tcr-form-group">
                <Form.Label className="tcr-form-label">
                  Task (Optional)
                </Form.Label>
                <SearchableSelect
                  value={reportData.TaskId}
                  onChange={handleTaskChange}
                  options={taskOptions}
                  placeholder={
                    !reportData.ProjectCode
                      ? "Select a project first"
                      : fetchingTasks
                        ? "Loading tasks..."
                        : tasks.length === 0
                          ? "No tasks available"
                          : "Select Task"
                  }
                  disabled={
                    !reportData.ProjectCode ||
                    tasks.length === 0 ||
                    fetchingTasks
                  }
                  loading={fetchingTasks}
                  optionLabelKey="name"
                  optionValueKey="id"
                  searchPlaceholder="Search task..."
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="tcr-row">
            <Col md={4} className="tcr-col">
              <Form.Group className="tcr-form-group">
                <Form.Label className="tcr-form-label">
                  From Date (Optional)
                </Form.Label>
                <Form.Control
                  type="date"
                  name="FromDate"
                  value={reportData.FromDate}
                  onChange={handleChange}
                  isInvalid={!!errors.FromDate}
                  className="tcr-form-control"
                />
              </Form.Group>
            </Col>

            <Col md={4} className="tcr-col">
              <Form.Group className="tcr-form-group">
                <Form.Label className="tcr-form-label">
                  To Date {reportData.FromDate && "(Required)"}
                </Form.Label>
                <Form.Control
                  type="date"
                  name="ToDate"
                  value={reportData.ToDate}
                  onChange={handleChange}
                  disabled={!reportData.FromDate}
                  required={!!reportData.FromDate}
                  isInvalid={!!errors.ToDate}
                  className="tcr-form-control"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.ToDate}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <div className="tcr-btn-container">
            <Button
              variant="primary"
              type="submit"
              className="tcr-btn-primary"
              disabled={loading || fetchingResources}
            >
              {loading ? "Generating..." : "Generate Report"}
            </Button>
            {showResults && reportResults.data.length > 0 && (
              <Button
                variant="success"
                onClick={downloadExcel}
                className="tcr-btn-success"
              >
                <i className="fas fa-file-excel"></i>
                Download Excel
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="info"
                onClick={handleViewDeleted}
                className="tcr-btn-info"
              >
                <i className="fas fa-trash-restore"></i>
                View Deleted
              </Button>
            )}
          </div>
        </Form>

        {showResults && (
          <div className="tcr-results-container">
            <h4 className="tcr-results-title">Report Results</h4>
            <div className="tcr-results-summary">
              <p>
                <strong>Message:</strong> {reportResults.message}
              </p>
              <p>
                <strong>Total Records:</strong> {reportResults.count}
              </p>
              <p>
                <strong>Total Hours:</strong>{" "}
                <span className="tcr-total-hours">{totalHours.toFixed(2)}</span>
              </p>
              {reportResults.resourceIds &&
                reportResults.resourceIds.length > 0 && (
                  <p>
                    <strong>Resources:</strong>{" "}
                    {reportResults.resourceIds.join(", ")}
                  </p>
                )}
              {reportResults.projectCode && (
                <p>
                  <strong>Project Code:</strong> {reportResults.projectCode}
                </p>
              )}
              {reportResults.taskId && (
                <p>
                  <strong>Task ID:</strong> {reportResults.taskId}
                </p>
              )}
              <p>
                <strong>Date Range:</strong>{" "}
                {reportResults.fromDate
                  ? new Date(reportResults.fromDate).toLocaleDateString()
                  : "All"}{" "}
                to{" "}
                {reportResults.toDate
                  ? new Date(reportResults.toDate).toLocaleDateString()
                  : "All"}
              </p>
            </div>

            {/* Hours Summary Section */}
            {/* Hours Summary Section with Tab/Switch Interface */}
            <div className="tcr-hours-summary">
              <div className="tcr-summary-header">
                <h5>Hours Summary</h5>
                <div className="tcr-summary-tabs">
                  <button
                    className={`tcr-summary-tab ${activeSummaryTab === "resource" ? "active" : ""}`}
                    onClick={() => setActiveSummaryTab("resource")}
                  >
                     By Resource
                  </button>
                  <button
                    className={`tcr-summary-tab ${activeSummaryTab === "project" ? "active" : ""}`}
                    onClick={() => setActiveSummaryTab("project")}
                  >
                    By Project
                  </button>
                  <button
                    className={`tcr-summary-tab ${activeSummaryTab === "status" ? "active" : ""}`}
                    onClick={() => setActiveSummaryTab("status")}
                  >
                    By Status
                  </button>
                </div>
              </div>

              {/* Summary by Resource Table */}
              {activeSummaryTab === "resource" &&
                hoursByResource.length > 0 && (
                  <div className="tcr-summary-section">
                    <div className="tcr-summary-table-wrapper">
                      <Table bordered size="sm" className="tcr-summary-table">
                        <thead>
                          <tr>
                            <th>Resource ID</th>
                            <th>User Name</th>
                            <th>Total Hours</th>
                            <th>Record Count</th>
                            <th>% of Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hoursByResource.map((resource, idx) => (
                            <tr key={idx}>
                              <td>{resource.resourceId}</td>
                              <td>{resource.userName}</td>
                              <td className="tcr-hours-value">
                                {resource.hours.toFixed(2)}
                              </td>
                              <td>{resource.recordCount}</td>
                              <td>
                                {((resource.hours / totalHours) * 100).toFixed(
                                  1,
                                )}
                                %
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <th colSpan="2">Grand Total</th>
                            <th className="tcr-hours-value">
                              {totalHours.toFixed(2)}
                            </th>
                            <th>{reportResults.count}</th>
                            <th>100%</th>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  </div>
                )}

              {/* Summary by Project Table */}
              {activeSummaryTab === "project" && hoursByProject.length > 0 && (
                <div className="tcr-summary-section">
                  <div className="tcr-summary-table-wrapper">
                    <Table bordered size="sm" className="tcr-summary-table">
                      <thead>
                        <tr>
                          <th>Project Code</th>
                          <th>Project Name</th>
                          <th>Total Hours</th>
                          <th>Record Count</th>
                          <th>% of Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hoursByProject.map((project, idx) => (
                          <tr key={idx}>
                            <td>{project.projectCode}</td>
                            <td>{project.projectName}</td>
                            <td className="tcr-hours-value">
                              {project.hours.toFixed(2)}
                            </td>
                            <td>{project.recordCount}</td>
                            <td>
                              {((project.hours / totalHours) * 100).toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th colSpan="2">Grand Total</th>
                          <th className="tcr-hours-value">
                            {totalHours.toFixed(2)}
                          </th>
                          <th>{reportResults.count}</th>
                          <th>100%</th>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                </div>
              )}

              {/* Summary by Status Table */}
              {activeSummaryTab === "status" && hoursByStatus.length > 0 && (
                <div className="tcr-summary-section">
                  <div className="tcr-summary-table-wrapper">
                    <Table bordered size="sm" className="tcr-summary-table">
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Total Hours</th>
                          <th>Record Count</th>
                          <th>% of Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hoursByStatus.map((status, idx) => (
                          <tr key={idx}>
                            <td>{status.status}</td>
                            <td className="tcr-hours-value">
                              {status.hours.toFixed(2)}
                            </td>
                            <td>{status.recordCount}</td>
                            <td>
                              {((status.hours / totalHours) * 100).toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th>Grand Total</th>
                          <th className="tcr-hours-value">
                            {totalHours.toFixed(2)}
                          </th>
                          <th>{reportResults.count}</th>
                          <th>100%</th>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                </div>
              )}
            </div>

            <div className="tcr-table-wrapper">
              <div className="tcr-table-scroll-container">
                {reportResults.data.length > 0 ? (
                  <>
                    <Table
                      striped
                      bordered
                      hover
                      responsive
                      className="tcr-data-table"
                    >
                      <thead>
                        <tr>
                          <th>DT Id</th>
                          <th>Resource ID</th>
                          <th>User Name</th>
                          <th>Date</th>
                          <th>Project Code</th>
                          <th>Project Name</th>
                          <th>Task ID</th>
                          <th>Task Name</th>
                          <th>Hours</th>
                          <th>Remarks</th>
                          <th>Location</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportResults.data.map((item, index) => (
                          <tr key={index}>
                            <td>{item.DTId}</td>
                            <td>{item.ResourceId || item.ResourceCode}</td>
                            <td>{item.UserName}</td>
                            <td>{item.Date}</td>
                            <td>{item.ProjectCode}</td>
                            <td>{item.ProjectName || "N/A"}</td>
                            <td>{item.TaskId}</td>
                            <td>{item.TaskName || "N/A"}</td>
                            <td className="tcr-hours-cell">{item.Hours}</td>
                            <td>{item.Remarks}</td>
                            <td>{item.Location}</td>
                            <td>
                              {item.StatusName ||
                                (item.Status === "W"
                                  ? "Pending"
                                  : item.Status === "A"
                                    ? "Approved"
                                    : item.Status === "R"
                                      ? "Rejected"
                                      : item.Status)}
                            </td>
                            <td className="tcr-actions-container">
                              <Button
                                variant="warning"
                                size="sm"
                                onClick={() => handleEditClick(item)}
                                className="tcr-btn-warning-sm"
                                title="Edit Timecard"
                              >
                                <i className="fas fa-edit"></i> Edit
                              </Button>
                              {isAdmin && (
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleDeleteClick(item)}
                                  className="tcr-btn-danger-sm"
                                  title="Delete Timecard"
                                >
                                  <i className="fas fa-trash"></i> Delete
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="tcr-total-row">
                          <td colSpan="8" className="tcr-total-label">
                            <strong>Total Hours:</strong>
                          </td>
                          <td className="tcr-total-hours-cell">
                            <strong>{totalHours.toFixed(2)}</strong>
                          </td>
                          <td colSpan="4"></td>
                        </tr>
                      </tfoot>
                    </Table>
                  </>
                ) : (
                  <div className="tcr-empty-state">
                    <p>No records found for the given criteria</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Timecard Modal - Updated with all editable fields */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
      >
        <Modal.Header closeButton className="tcr-modal-header">
          <Modal.Title className="tcr-modal-title">
            Edit Timecard - DTId: {selectedTimecard?.DTId}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="tcr-modal-body">
          <Form>
            {/* Read-only User Info */}
            <Row>
              <Col md={6}>
                <Form.Group className="tcr-form-group">
                  <Form.Label className="tcr-form-label">User Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedTimecard?.UserName || ""}
                    disabled
                    readOnly
                    className="tcr-form-control"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="tcr-form-group">
                  <Form.Label className="tcr-form-label">
                    Resource ID
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      selectedTimecard?.ResourceId ||
                      selectedTimecard?.ResourceCode ||
                      ""
                    }
                    disabled
                    readOnly
                    className="tcr-form-control"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Editable Date and Status */}
            <Row>
              <Col md={6}>
                <Form.Group className="tcr-form-group">
                  <Form.Label className="tcr-form-label">Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={editFormData.date}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, date: e.target.value })
                    }
                    className="tcr-form-control"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="tcr-form-group">
                  <Form.Label className="tcr-form-label">Status</Form.Label>
                  <Form.Select
                    value={editFormData.status}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        status: e.target.value,
                      })
                    }
                    className="tcr-form-select"
                  >
                    <option value="W">Pending</option>
                    <option value="A">Approved</option>
                    <option value="R">Rejected</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Editable Project */}
            <Row>
              <Col md={12}>
                <Form.Group className="tcr-form-group">
                  <Form.Label className="tcr-form-label">Project *</Form.Label>
                  <SearchableSelect
                    value={editFormData.projectCode}
                    onChange={handleEditProjectChange}
                    options={editProjectOptions}
                    placeholder="Select Project"
                    disabled={fetchingEditProjects || editProjects.length === 0}
                    loading={fetchingEditProjects}
                    optionLabelKey="name"
                    optionValueKey="id"
                    searchPlaceholder="Search project..."
                    noResultsText="No projects found"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Editable Task */}
            <Row>
              <Col md={12}>
                <Form.Group className="tcr-form-group">
                  <Form.Label className="tcr-form-label">Task</Form.Label>
                  <SearchableSelect
                    value={editFormData.taskId}
                    onChange={handleEditTaskChange}
                    options={editTaskOptions}
                    placeholder={
                      !editFormData.projectCode
                        ? "Select a project first"
                        : fetchingEditTasks
                          ? "Loading tasks..."
                          : editTasks.length === 0
                            ? "No tasks available"
                            : "Select Task"
                    }
                    disabled={
                      !editFormData.projectCode ||
                      editTasks.length === 0 ||
                      fetchingEditTasks
                    }
                    loading={fetchingEditTasks}
                    optionLabelKey="name"
                    optionValueKey="id"
                    searchPlaceholder="Search task..."
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Editable Hours, Location, Remarks */}
            <Row>
              <Col md={4}>
                <Form.Group className="tcr-form-group">
                  <Form.Label className="tcr-form-label">Hours *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.5"
                    min="0"
                    max="24"
                    value={editFormData.hours}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        hours: e.target.value,
                      })
                    }
                    required
                    className="tcr-form-control"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="tcr-form-group">
                  <Form.Label className="tcr-form-label">Location</Form.Label>
                  <Form.Control
                    type="text"
                    value={editFormData.location}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        location: e.target.value,
                      })
                    }
                    placeholder="Enter location"
                    className="tcr-form-control"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="tcr-form-group">
                  <Form.Label className="tcr-form-label">
                    Resource ID
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={editFormData.resourceId}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        resourceId: e.target.value,
                      })
                    }
                    placeholder="Enter Resource ID"
                    className="tcr-form-control"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Editable Remarks */}
            <Form.Group className="tcr-form-group">
              <Form.Label className="tcr-form-label">Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editFormData.remarks}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, remarks: e.target.value })
                }
                placeholder="Enter remarks"
                className="tcr-form-control"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="tcr-modal-footer">
          <Button
            variant="secondary"
            onClick={() => setShowEditModal(false)}
            className="tcr-btn-secondary"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateTimecard}
            disabled={updating}
            className="tcr-btn-primary"
          >
            {updating ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
      >
        <Modal.Header closeButton className="tcr-modal-header">
          <Modal.Title className="tcr-modal-title">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tcr-modal-body">
          <p>
            Are you sure you want to delete timecard with DTId:{" "}
            <strong>{timecardToDelete?.DTId}</strong>?
          </p>
          <p className="tcr-delete-warning">
            This action can be undone by restoring from deleted items.
          </p>
          <hr />
          <p>
            <strong>Details:</strong>
          </p>
          <div className="tcr-delete-details">
            <ul>
              <li>User: {timecardToDelete?.UserName}</li>
              <li>Date: {timecardToDelete?.Date}</li>
              <li>
                Project: {timecardToDelete?.ProjectCode} -{" "}
                {timecardToDelete?.ProjectName}
              </li>
              <li>Hours: {timecardToDelete?.Hours}</li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer className="tcr-modal-footer">
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirm(false)}
            className="tcr-btn-secondary"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={updating}
            className="tcr-btn-danger-sm"
          >
            {updating ? "Deleting..." : "Yes, Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Deleted Timecards Modal (Admin only) */}
      <Modal
        show={showDeletedModal}
        onHide={() => setShowDeletedModal(false)}
        size="xl"
      >
        <Modal.Header closeButton className="tcr-modal-header">
          <Modal.Title className="tcr-modal-title">
            Deleted Timecards
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="tcr-modal-body">
          {deletedTimecards.length > 0 ? (
            <div className="tcr-deleted-table-container">
              <Table
                striped
                bordered
                hover
                size="sm"
                className="tcr-deleted-table"
              >
                <thead>
                  <tr>
                    <th>DTId</th>
                    <th>User Name</th>
                    <th>Date</th>
                    <th>Project Code</th>
                    <th>Task ID</th>
                    <th>Hours</th>
                    <th>Remarks</th>
                    <th>Deleted By</th>
                    <th>Deleted On</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedTimecards.map((item) => (
                    <tr key={item.dtid}>
                      <td>{item.dtid}</td>
                      <td>{item.userName}</td>
                      <td>
                        {item.date
                          ? new Date(item.date).toLocaleDateString()
                          : ""}
                      </td>
                      <td>{item.projectCode}</td>
                      <td>{item.tasks}</td>
                      <td>{item.hours}</td>
                      <td>{item.remarks}</td>
                      <td>{item.changedBy}</td>
                      <td>
                        {item.changedOnDate
                          ? new Date(item.changedOnDate).toLocaleDateString()
                          : ""}
                      </td>
                      <td>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleRestoreTimecard(item.dtid)}
                          disabled={updating}
                          className="tcr-btn-restore-sm"
                        >
                          <i className="fas fa-trash-restore"></i> Restore
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p className="tcr-empty-state">No deleted timecards found</p>
          )}
        </Modal.Body>
        <Modal.Footer className="tcr-modal-footer">
          <Button
            variant="secondary"
            onClick={() => setShowDeletedModal(false)}
            className="tcr-btn-secondary"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {(loading ||
        fetchingResources ||
        fetchingProjects ||
        fetchingTasks ||
        updating) && (
        <div className="tcr-loading-overlay">
          <Sample14 />
        </div>
      )}
    </div>
  );
};

export default TimeCardReport;

// import { useState, useEffect, useCallback } from "react";
// import {
//   generateTimeCardReport,
//   updateTimecard,
//   deleteTimecard,
//   restoreTimecard,
//   getDeletedTimecards,
// } from "../Api/TimeCardReport";
// import { getResourcesByOrgId } from "../Api/ResourceList";
// import {
//   fetchProjectBaseByOrg,
//   fetchProjectBaseByOrgAndMember,
// } from "../Api/ViewProjects";
// import {
//   getTasksByProject,
//   getTasksByAssignedTo,
//   getAllTaskStatus,
//   getContactGuid,
// } from "../Api/TaskMaster";
// import {
//   Container,
//   Form,
//   Button,
//   Row,
//   Col,
//   Table,
//   Modal,
// } from "react-bootstrap";
// import { toast } from "react-toastify";
// import Sample14 from "../Components/Logo";
// import SearchableSelect from "../Pages/ProjectComponents/SearchableSelect";
// import SearchableMultiSelect from "../Pages/ProjectComponents/SearchableMultiSelect";
// import * as XLSX from "xlsx";
// import { saveAs } from "file-saver";
// import "./TimeCardReport.css";

// const TimeCardReport = () => {
//   const [loading, setLoading] = useState(false);
//   const [fetchingResources, setFetchingResources] = useState(false);
//   const [fetchingProjects, setFetchingProjects] = useState(false);
//   const [fetchingTasks, setFetchingTasks] = useState(false);
//   const [resources, setResources] = useState([]);
//   const [projects, setProjects] = useState([]);
//   const [tasks, setTasks] = useState([]);
//   const [userContactGuid, setUserContactGuid] = useState(null);
//   const [taskStatusMap, setTaskStatusMap] = useState({});
//   const [reportData, setReportData] = useState({
//     ResourceCodes: [],
//     ProjectCode: "",
//     TaskId: "",
//     FromDate: "",
//     ToDate: "",
//   });
//   const [reportResults, setReportResults] = useState({
//     data: [],
//     message: "",
//     count: 0,
//     resourceIds: [],
//     fromDate: "",
//     toDate: "",
//   });
//   const [showResults, setShowResults] = useState(false);
//   const [errors, setErrors] = useState({});

//   // Modal states
//   const [showEditModal, setShowEditModal] = useState(false);
//   const [selectedTimecard, setSelectedTimecard] = useState(null);
//   const [editFormData, setEditFormData] = useState({
//     date: "",
//     projectCode: "",
//     taskId: "",
//     tasks: "",
//     hours: "",
//     remarks: "",
//     location: "",
//     status: "",
//     resourceId: "",
//   });
//   const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
//   const [timecardToDelete, setTimecardToDelete] = useState(null);
//   const [showDeletedModal, setShowDeletedModal] = useState(false);
//   const [deletedTimecards, setDeletedTimecards] = useState([]);
//   const [updating, setUpdating] = useState(false);
//   const [editProjects, setEditProjects] = useState([]);
//   const [editTasks, setEditTasks] = useState([]);
//   const [fetchingEditProjects, setFetchingEditProjects] = useState(false);
//   const [fetchingEditTasks, setFetchingEditTasks] = useState(false);

//   // Define admin roles
//   const ADMIN_ROLES = ["Admin"];

//   // Check if user has admin role
//   const hasAdminRole = (roles) => {
//     if (!roles || !Array.isArray(roles)) return false;
//     return roles.some((role) => ADMIN_ROLES.includes(role));
//   };

//   // Check if user has view permission for all tasks
//   const hasViewPermission = (roles) => {
//     if (!roles || !Array.isArray(roles)) return false;
//     return roles.some((role) =>
//       ["Admin", "Project_Manager", "Project_Commercial"].includes(role),
//     );
//   };

//   // Get current user from localStorage
//   const getCurrentUser = () => {
//     const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//     return {
//       userId: userDetails?.userId || localStorage.getItem("memberId"),
//       roles: userDetails?.roles || [],
//       username: userDetails?.username || "",
//     };
//   };

//   const currentUser = getCurrentUser();
//   const isAdmin = hasAdminRole(currentUser.roles);

//   // Load all task statuses for display
//   const loadAllTaskStatuses = async () => {
//     try {
//       const statuses = await getAllTaskStatus();
//       const statusMap = {};
//       statuses.forEach((status) => {
//         statusMap[status.statusGuid.toLowerCase()] = status.statusName;
//       });
//       setTaskStatusMap(statusMap);
//     } catch (error) {
//       console.error("Error loading task statuses:", error);
//     }
//   };

//   // Get task status display name
//   const getTaskStatusName = (statusGuid) => {
//     if (!statusGuid) return "No Status";
//     const lowerGuid = statusGuid.toLowerCase();
//     return taskStatusMap[lowerGuid] || statusGuid;
//   };

//   // Fetch user's contact GUID
//   const fetchUserContactGuid = async (orgId, userId) => {
//     try {
//       const response = await getContactGuid(orgId, userId);
//       const contactGuid = response.contactGuid;
//       setUserContactGuid(contactGuid);
//       return contactGuid;
//     } catch (error) {
//       console.error("Error fetching user contact GUID:", error);
//       return null;
//     }
//   };

//   // Load projects for edit modal
//   const loadEditProjects = async () => {
//     setFetchingEditProjects(true);
//     try {
//       const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//       const orgId = userDetails?.organisationId || localStorage.getItem("orgId");
//       const currentUserId = userDetails?.userId || localStorage.getItem("memberId");
//       const userRoles = userDetails?.roles || [];
//       const admin = hasAdminRole(userRoles);

//       let projectsData;
//       if (admin) {
//         projectsData = await fetchProjectBaseByOrg(orgId);
//       } else {
//         projectsData = await fetchProjectBaseByOrgAndMember(orgId, currentUserId);
//       }

//       const mappedProjects = (projectsData || [])
//         .filter((project) => project.active === 1)
//         .map((project) => ({
//           projectId: project.project_id || project.projectId,
//           prTitle: project.pr_Title || project.prTitle,
//         }));

//       setEditProjects(mappedProjects);
//     } catch (error) {
//       console.error("Error loading projects for edit:", error);
//       toast.error("Failed to load projects");
//     } finally {
//       setFetchingEditProjects(false);
//     }
//   };

//   // Load tasks for edit modal based on selected project
//   const loadEditTasks = async (projectId) => {
//     if (!projectId) {
//       setEditTasks([]);
//       return;
//     }

//     setFetchingEditTasks(true);
//     try {
//       const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//       const userRoles = userDetails?.roles || [];
//       let tasksData = [];
//       const admin = hasViewPermission(userRoles);

//       if (admin) {
//         tasksData = await getTasksByProject(projectId);
//       } else {
//         if (!userContactGuid) {
//           setEditTasks([]);
//           return;
//         }
//         const assignedTasks = await getTasksByAssignedTo(userContactGuid);
//         tasksData = assignedTasks.filter(
//           (task) =>
//             task.projectBelong === projectId ||
//             task.projectBelong === parseInt(projectId),
//         );
//       }

//       const mappedTasks = tasksData.map((task) => ({
//         taskId: task.taskId,
//         taskName: task.taskName,
//         displayValue: `${task.taskId} - ${task.taskName}`,
//       }));

//       setEditTasks(mappedTasks);
//     } catch (error) {
//       console.error("Error loading tasks for edit:", error);
//       setEditTasks([]);
//     } finally {
//       setFetchingEditTasks(false);
//     }
//   };

//   // Load projects based on user role
//   const loadProjects = async (orgId, userId, userRoles) => {
//     setFetchingProjects(true);
//     try {
//       let projectsData;
//       const admin = hasAdminRole(userRoles);

//       if (admin) {
//         projectsData = await fetchProjectBaseByOrg(orgId);
//       } else {
//         projectsData = await fetchProjectBaseByOrgAndMember(orgId, userId);
//       }

//       const mappedProjects = (projectsData || [])
//         .filter((project) => project.active === 1)
//         .map((project) => ({
//           projectId: project.project_id || project.projectId,
//           prTitle: project.pr_Title || project.prTitle,
//           ...project,
//         }));

//       setProjects(mappedProjects);
//       return mappedProjects;
//     } catch (error) {
//       console.error("Error loading projects:", error);
//       toast.error("Failed to load projects");
//       return [];
//     } finally {
//       setFetchingProjects(false);
//     }
//   };

//   // Load tasks based on selected project
//   const loadTasks = useCallback(async (projectId, userRoles, contactGuid) => {
//     if (!projectId) {
//       setTasks([]);
//       return;
//     }

//     setFetchingTasks(true);
//     try {
//       let tasksData = [];
//       const admin = hasViewPermission(userRoles);

//       if (admin) {
//         tasksData = await getTasksByProject(projectId);
//       } else {
//         if (!contactGuid) {
//           setTasks([]);
//           return;
//         }
//         const assignedTasks = await getTasksByAssignedTo(contactGuid);
//         tasksData = assignedTasks.filter(
//           (task) =>
//             task.projectBelong === projectId ||
//             task.projectBelong === parseInt(projectId),
//         );
//       }

//       const mappedTasks = tasksData.map((task) => ({
//         taskId: task.taskId,
//         taskName: task.taskName,
//         taskStatus: getTaskStatusName(task.taskStatus),
//         displayValue: `${task.taskId} - ${task.taskName}`,
//       }));

//       setTasks(mappedTasks);
//     } catch (error) {
//       console.error("Error loading tasks:", error);
//       setTasks([]);
//     } finally {
//       setFetchingTasks(false);
//     }
//   }, []);

//   // Excel Download Function
//   const downloadExcel = () => {
//     if (!reportResults.data.length) {
//       toast.warning("No data to export");
//       return;
//     }

//     try {
//       const excelData = reportResults.data.map((item) => ({
//         "DT Id": item.DTId || "",
//         "Resource ID": item.ResourceId || item.ResourceCode || "",
//         "User Name": item.UserName || "",
//         Date: item.Date || "",
//         "Project Code": item.ProjectCode || "",
//         "Project Name": item.ProjectName || "N/A",
//         "Task ID": item.TaskId || "",
//         "Task Name": item.TaskName || "N/A",
//         Hours: item.Hours || "",
//         Remarks: item.Remarks || "",
//         Location: item.Location || "",
//         Status:
//           item.StatusName ||
//           (item.Status === "W"
//             ? "Pending"
//             : item.Status === "A"
//               ? "Approved"
//               : item.Status === "R"
//                 ? "Rejected"
//                 : item.Status),
//       }));

//       const worksheet = XLSX.utils.json_to_sheet(excelData);
//       const colWidths = [
//         { wch: 10 },
//         { wch: 15 },
//         { wch: 20 },
//         { wch: 12 },
//         { wch: 15 },
//         { wch: 35 },
//         { wch: 15 },
//         { wch: 35 },
//         { wch: 10 },
//         { wch: 40 },
//         { wch: 20 },
//         { wch: 12 },
//       ];
//       worksheet["!cols"] = colWidths;

//       const workbook = XLSX.utils.book_new();
//       XLSX.utils.book_append_sheet(workbook, worksheet, "TimeCardReport");

//       const timestamp = new Date()
//         .toISOString()
//         .replace(/[:.]/g, "-")
//         .slice(0, 19);
//       const filename = `TimeCardReport_${timestamp}.xlsx`;
//       const excelBuffer = XLSX.write(workbook, {
//         bookType: "xlsx",
//         type: "array",
//       });
//       const blob = new Blob([excelBuffer], {
//         type: "application/octet-stream",
//       });
//       saveAs(blob, filename);

//       toast.success(
//         `Excel file downloaded successfully! (${reportResults.data.length} records)`,
//       );
//     } catch (error) {
//       console.error("Error downloading Excel:", error);
//       toast.error("Failed to download Excel file");
//     }
//   };

//   // Edit Timecard Handlers
//   const handleEditClick = async (timecard) => {
//     // Load projects for edit modal
//     await loadEditProjects();

//     setSelectedTimecard(timecard);
//     setEditFormData({
//       date: timecard.Date || "",
//       projectCode: timecard.ProjectCode || "",
//       taskId: timecard.TaskId || "",
//       tasks: timecard.TaskId || "", // Task ID as string
//       hours: timecard.Hours || "",
//       remarks: timecard.Remarks || "",
//       location: timecard.Location || "",
//       status: timecard.Status || "W",
//       resourceId: timecard.ResourceId || "",
//     });

//     // Load tasks for the selected project
//     if (timecard.ProjectCode) {
//       await loadEditTasks(timecard.ProjectCode);
//     }

//     setShowEditModal(true);
//   };

//   const handleEditProjectChange = async (projectCode) => {
//     setEditFormData(prev => ({
//       ...prev,
//       projectCode: projectCode,
//       taskId: "",
//       tasks: "",
//     }));
//     if (projectCode) {
//       await loadEditTasks(projectCode);
//     } else {
//       setEditTasks([]);
//     }
//   };

//   const handleEditTaskChange = (taskId) => {
//     setEditFormData(prev => ({
//       ...prev,
//       taskId: taskId,
//       tasks: taskId,
//     }));
//   };

//   const handleUpdateTimecard = async () => {
//     setUpdating(true);
//     try {
//       const updateData = {
//         date: editFormData.date,
//         projectCode: editFormData.projectCode,
//         hours: parseFloat(editFormData.hours),
//         tasks: editFormData.tasks,
//         remarks: editFormData.remarks,
//         location: editFormData.location,
//         status: editFormData.status,
//         resourceId: editFormData.resourceId,
//       };

//       const response = await updateTimecard(selectedTimecard.DTId, updateData);
//       toast.success(response.message || "Timecard updated successfully!");

//       // Refresh the report
//       await refreshReport();
//       setShowEditModal(false);
//     } catch (error) {
//       toast.error(error.message || "Error updating timecard");
//     } finally {
//       setUpdating(false);
//     }
//   };

//   // Delete Timecard Handlers (Admin only)
//   const handleDeleteClick = (timecard) => {
//     if (!isAdmin) {
//       toast.warning("Only administrators can delete timecards");
//       return;
//     }
//     setTimecardToDelete(timecard);
//     setShowDeleteConfirm(true);
//   };

//   const handleConfirmDelete = async () => {
//     setUpdating(true);
//     try {
//       const response = await deleteTimecard(timecardToDelete.DTId);
//       toast.success(response.message || "Timecard deleted successfully!");

//       // Refresh the report
//       await refreshReport();
//       setShowDeleteConfirm(false);
//       setTimecardToDelete(null);
//     } catch (error) {
//       toast.error(error.message || "Error deleting timecard");
//     } finally {
//       setUpdating(false);
//     }
//   };

//   // View Deleted Timecards (Admin only)
//   const handleViewDeleted = async () => {
//     if (!isAdmin) {
//       toast.warning("Only administrators can view deleted timecards");
//       return;
//     }

//     try {
//       const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//       const organisationId =
//         userDetails?.organisationId || localStorage.getItem("orgId");
//       const response = await getDeletedTimecards(organisationId);
//       setDeletedTimecards(response.data);
//       setShowDeletedModal(true);
//       toast.success(response.message);
//     } catch (error) {
//       toast.error(error.message || "Error fetching deleted timecards");
//     }
//   };

//   // Restore Timecard Handler (Admin only)
//   const handleRestoreTimecard = async (dtid) => {
//     if (!isAdmin) {
//       toast.warning("Only administrators can restore timecards");
//       return;
//     }

//     setUpdating(true);
//     try {
//       const response = await restoreTimecard(dtid);
//       toast.success(response.message || "Timecard restored successfully!");

//       // Refresh both reports
//       await refreshReport();
//       const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//       const organisationId =
//         userDetails?.organisationId || localStorage.getItem("orgId");
//       const deletedResponse = await getDeletedTimecards(organisationId);
//       setDeletedTimecards(deletedResponse.data);
//     } catch (error) {
//       toast.error(error.message || "Error restoring timecard");
//     } finally {
//       setUpdating(false);
//     }
//   };

//   // Refresh report after edit/delete
//   const refreshReport = async () => {
//     try {
//       const response = await generateTimeCardReport({
//         ResourceCodes: reportData.ResourceCodes,
//         ProjectCode: reportData.ProjectCode,
//         TaskId: reportData.TaskId,
//         FromDate: reportData.FromDate,
//         ToDate: reportData.ToDate,
//       });
//       setReportResults(response);
//     } catch (error) {
//       console.error("Error refreshing report:", error);
//     }
//   };

//   // Fetch resources based on user role
//   useEffect(() => {
//     const loadInitialData = async () => {
//       setFetchingResources(true);
//       try {
//         const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//         const userRoles = userDetails?.roles || [];
//         const orgId =
//           userDetails?.organisationId || localStorage.getItem("orgId");
//         const currentUserId =
//           userDetails?.userId || localStorage.getItem("memberId");

//         await loadAllTaskStatuses();
//         await fetchUserContactGuid(orgId, currentUserId);
//         await loadProjects(orgId, currentUserId, userRoles);

//         const allResources = await getResourcesByOrgId(orgId);
//         const activeResources = allResources.filter(
//           (resource) => resource.active === true,
//         );

//         const admin = hasAdminRole(userRoles);

//         if (admin) {
//           const formattedResources = activeResources.map((resource) => ({
//             id: resource.resourceId,
//             name: `${resource.resourceId} - ${resource.name} (${resource.emailId})`,
//           }));
//           setResources(formattedResources);
//         } else {
//           const currentUserResource = activeResources.find(
//             (resource) => resource.mavUserId === currentUserId,
//           );

//           if (currentUserResource) {
//             const formattedResource = {
//               id: currentUserResource.resourceId,
//               name: `${currentUserResource.resourceId} - ${currentUserResource.name}`,
//             };
//             setResources([formattedResource]);
//             setReportData((prev) => ({
//               ...prev,
//               ResourceCodes: [currentUserResource.resourceId],
//             }));
//           } else {
//             toast.warning("No resource profile found for your account");
//             setResources([]);
//           }
//         }
//       } catch (error) {
//         console.error("Error loading data:", error);
//         toast.error(`Error loading data: ${error.message || "Unknown error"}`);
//       } finally {
//         setFetchingResources(false);
//       }
//     };

//     loadInitialData();
//   }, []);

//   // Load tasks when project changes
//   useEffect(() => {
//     const loadTasksAsync = async () => {
//       if (reportData.ProjectCode) {
//         const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//         const userRoles = userDetails?.roles || [];

//         await loadTasks(reportData.ProjectCode, userRoles, userContactGuid);

//         setReportData((prev) => ({
//           ...prev,
//           TaskId: "",
//         }));
//       } else {
//         setTasks([]);
//       }
//     };

//     loadTasksAsync();
//   }, [reportData.ProjectCode, loadTasks, userContactGuid]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setReportData((prev) => ({ ...prev, [name]: value }));

//     if (errors[name]) {
//       setErrors((prev) => ({ ...prev, [name]: null }));
//     }

//     if (name === "FromDate" && !value) {
//       setReportData((prev) => ({ ...prev, ToDate: "" }));
//       setErrors((prev) => ({ ...prev, ToDate: null }));
//     }
//   };

//   const handleResourceChange = (selectedResources) => {
//     setReportData((prev) => ({
//       ...prev,
//       ResourceCodes: selectedResources,
//     }));
//   };

//   const handleProjectChange = (projectCode) => {
//     setReportData((prev) => ({
//       ...prev,
//       ProjectCode: projectCode,
//       TaskId: "",
//     }));
//   };

//   const handleTaskChange = (taskId) => {
//     setReportData((prev) => ({
//       ...prev,
//       TaskId: taskId,
//     }));
//   };

//   const validateForm = () => {
//     const newErrors = {};
//     const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//     const admin = hasAdminRole(userDetails?.roles || []);

//     if (!admin && reportData.ResourceCodes.length === 0) {
//       newErrors.ResourceCodes = "Resource Code is required";
//     }

//     if (reportData.FromDate && !reportData.ToDate) {
//       newErrors.ToDate = "To Date is required when From Date is entered";
//     }

//     if (reportData.FromDate && reportData.ToDate) {
//       if (new Date(reportData.ToDate) < new Date(reportData.FromDate)) {
//         newErrors.ToDate = "To Date must be after From Date";
//       }
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!validateForm()) {
//       return;
//     }

//     setLoading(true);
//     try {
//       const response = await generateTimeCardReport({
//         ResourceCodes: reportData.ResourceCodes,
//         ProjectCode: reportData.ProjectCode,
//         TaskId: reportData.TaskId,
//         FromDate: reportData.FromDate,
//         ToDate: reportData.ToDate,
//       });
//       console.log(response);
//       setReportResults(response);
//       setShowResults(true);

//       if (reportData.ResourceCodes.length === 0) {
//         toast.success(
//           `✅ Report Generated for All Resources (${response.count} records)`,
//         );
//       } else if (reportData.ResourceCodes.length === 1) {
//         toast.success(
//           `✅ ${response.message || "Report Generated Successfully"}`,
//         );
//       } else {
//         toast.success(
//           `✅ Report Generated for ${reportData.ResourceCodes.length} Resources (${response.count} records)`,
//         );
//       }
//     } catch (error) {
//       toast.error(`❌ ${error.message || "Error Generating Report"}`);
//       setShowResults(false);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const projectOptions = projects.map((project) => ({
//     id: project.projectId,
//     name: `${project.projectId} - ${project.prTitle}`,
//   }));

//   const taskOptions = tasks.map((task) => ({
//     id: task.taskId,
//     name: task.displayValue,
//   }));

//   const editProjectOptions = editProjects.map((project) => ({
//     id: project.projectId,
//     name: `${project.projectId} - ${project.prTitle}`,
//   }));

//   const editTaskOptions = editTasks.map((task) => ({
//     id: task.taskId,
//     name: task.displayValue,
//   }));

//   return (
//     <div className="tcr-main-container">
//       <div className="tcr-fields-container">
//         <h2 className="tcr-title">Time Card Report</h2>

//         <div className="tcr-alert-info">
//           <i className="fas fa-info-circle"></i>
//           {isAdmin ? (
//             <span>
//               <strong>Admin Access:</strong> You can filter time cards by
//               multiple Resources, Project, and Task. Leave Resources empty to
//               view all resources, or select specific ones for focused reports.
//               <br />
//               <strong>Delete Access:</strong> As admin, you can delete and
//               restore timecards.
//             </span>
//           ) : (
//             <span>
//               <strong>Limited Access:</strong> You can only view your own time
//               cards.
//               <br />
//               <strong>Edit Access:</strong> You can edit your own timecards.
//             </span>
//           )}
//         </div>

//         <Form onSubmit={handleSubmit} className="tcr-fields-form">
//           <Row className="tcr-row">
//             <Col md={4} className="tcr-col">
//               <Form.Group className="tcr-form-group">
//                 <Form.Label className="tcr-form-label">
//                   Resources{" "}
//                   {!isAdmin && <span className="tcr-text-danger">**</span>}
//                 </Form.Label>
//                 {isAdmin ? (
//                   <SearchableMultiSelect
//                     value={reportData.ResourceCodes}
//                     onChange={handleResourceChange}
//                     options={resources}
//                     placeholder="Select Resources (Leave empty for All Resources)"
//                     disabled={fetchingResources}
//                     loading={fetchingResources}
//                     optionLabelKey="name"
//                     optionValueKey="id"
//                     searchPlaceholder="Search resources..."
//                     noResultsText="No resources found"
//                   />
//                 ) : (
//                   <Form.Select
//                     name="ResourceCode"
//                     value={reportData.ResourceCodes[0] || ""}
//                     onChange={(e) => handleResourceChange([e.target.value])}
//                     required={!isAdmin}
//                     disabled={fetchingResources || resources.length === 1}
//                     isInvalid={!!errors.ResourceCodes}
//                     className="tcr-form-select"
//                   >
//                     {resources.map((resource) => (
//                       <option key={resource.id} value={resource.id}>
//                         {resource.name}
//                       </option>
//                     ))}
//                   </Form.Select>
//                 )}
//                 {errors.ResourceCodes && (
//                   <Form.Text className="text-danger">
//                     {errors.ResourceCodes}
//                   </Form.Text>
//                 )}
//                 {isAdmin && !fetchingResources && (
//                   <small className="tcr-text-muted tcr-text-info">
//                     <i className="fas fa-info-circle tcr-info-icon"></i>
//                     Select specific resources or leave empty to view all
//                     resources
//                   </small>
//                 )}
//               </Form.Group>
//             </Col>

//             <Col md={4} className="tcr-col">
//               <Form.Group className="tcr-form-group">
//                 <Form.Label className="tcr-form-label">
//                   Project (Optional)
//                 </Form.Label>
//                 <SearchableSelect
//                   value={reportData.ProjectCode}
//                   onChange={handleProjectChange}
//                   options={projectOptions}
//                   placeholder="Select Project"
//                   disabled={fetchingProjects || projects.length === 0}
//                   loading={fetchingProjects}
//                   optionLabelKey="name"
//                   optionValueKey="id"
//                   searchPlaceholder="Search project..."
//                   noResultsText="No projects found"
//                 />
//               </Form.Group>
//             </Col>

//             <Col md={4} className="tcr-col">
//               <Form.Group className="tcr-form-group">
//                 <Form.Label className="tcr-form-label">
//                   Task (Optional)
//                 </Form.Label>
//                 <SearchableSelect
//                   value={reportData.TaskId}
//                   onChange={handleTaskChange}
//                   options={taskOptions}
//                   placeholder={
//                     !reportData.ProjectCode
//                       ? "Select a project first"
//                       : fetchingTasks
//                         ? "Loading tasks..."
//                         : tasks.length === 0
//                           ? "No tasks available"
//                           : "Select Task"
//                   }
//                   disabled={
//                     !reportData.ProjectCode ||
//                     tasks.length === 0 ||
//                     fetchingTasks
//                   }
//                   loading={fetchingTasks}
//                   optionLabelKey="name"
//                   optionValueKey="id"
//                   searchPlaceholder="Search task..."
//                 />
//               </Form.Group>
//             </Col>
//           </Row>

//           <Row className="tcr-row">
//             <Col md={4} className="tcr-col">
//               <Form.Group className="tcr-form-group">
//                 <Form.Label className="tcr-form-label">
//                   From Date (Optional)
//                 </Form.Label>
//                 <Form.Control
//                   type="date"
//                   name="FromDate"
//                   value={reportData.FromDate}
//                   onChange={handleChange}
//                   isInvalid={!!errors.FromDate}
//                   className="tcr-form-control"
//                 />
//               </Form.Group>
//             </Col>

//             <Col md={4} className="tcr-col">
//               <Form.Group className="tcr-form-group">
//                 <Form.Label className="tcr-form-label">
//                   To Date {reportData.FromDate && "(Required)"}
//                 </Form.Label>
//                 <Form.Control
//                   type="date"
//                   name="ToDate"
//                   value={reportData.ToDate}
//                   onChange={handleChange}
//                   disabled={!reportData.FromDate}
//                   required={!!reportData.FromDate}
//                   isInvalid={!!errors.ToDate}
//                   className="tcr-form-control"
//                 />
//                 <Form.Control.Feedback type="invalid">
//                   {errors.ToDate}
//                 </Form.Control.Feedback>
//               </Form.Group>
//             </Col>
//           </Row>

//           <div className="tcr-btn-container">
//             <Button
//               variant="primary"
//               type="submit"
//               className="tcr-btn-primary"
//               disabled={loading || fetchingResources}
//             >
//               {loading ? "Generating..." : "Generate Report"}
//             </Button>
//             {showResults && reportResults.data.length > 0 && (
//               <Button
//                 variant="success"
//                 onClick={downloadExcel}
//                 className="tcr-btn-success"
//               >
//                 <i className="fas fa-file-excel"></i>
//                 Download Excel
//               </Button>
//             )}
//             {isAdmin && (
//               <Button
//                 variant="info"
//                 onClick={handleViewDeleted}
//                 className="tcr-btn-info"
//               >
//                 <i className="fas fa-trash-restore"></i>
//                 View Deleted
//               </Button>
//             )}
//           </div>
//         </Form>

//         {showResults && (
//           <div className="tcr-results-container">
//             <h4 className="tcr-results-title">Report Results</h4>
//             <div className="tcr-results-summary">
//               <p>
//                 <strong>Message:</strong> {reportResults.message}
//               </p>
//               <p>
//                 <strong>Total Records:</strong> {reportResults.count}
//               </p>
//               {reportResults.resourceIds &&
//                 reportResults.resourceIds.length > 0 && (
//                   <p>
//                     <strong>Resources:</strong>{" "}
//                     {reportResults.resourceIds.join(", ")}
//                   </p>
//                 )}
//               {reportResults.projectCode && (
//                 <p>
//                   <strong>Project Code:</strong> {reportResults.projectCode}
//                 </p>
//               )}
//               {reportResults.taskId && (
//                 <p>
//                   <strong>Task ID:</strong> {reportResults.taskId}
//                 </p>
//               )}
//               <p>
//                 <strong>Date Range:</strong>{" "}
//                 {reportResults.fromDate
//                   ? new Date(reportResults.fromDate).toLocaleDateString()
//                   : "All"}{" "}
//                 to{" "}
//                 {reportResults.toDate
//                   ? new Date(reportResults.toDate).toLocaleDateString()
//                   : "All"}
//               </p>
//             </div>
//             <div className="tcr-table-wrapper">
//               <div className="tcr-table-scroll-container">
//                 {reportResults.data.length > 0 ? (
//                   <Table
//                     striped
//                     bordered
//                     hover
//                     responsive
//                     className="tcr-data-table"
//                   >
//                     <thead>
//                       <tr>
//                         <th>DT Id</th>
//                         <th>Resource ID</th>
//                         <th>User Name</th>
//                         <th>Date</th>
//                         <th>Project Code</th>
//                         <th>Project Name</th>
//                         <th>Task ID</th>
//                         <th>Task Name</th>
//                         <th>Hours</th>
//                         <th>Remarks</th>
//                         <th>Location</th>
//                         <th>Status</th>
//                         <th>Actions</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {reportResults.data.map((item, index) => (
//                         <tr key={index}>
//                           <td>{item.DTId}</td>
//                           <td>{item.ResourceId || item.ResourceCode}</td>
//                           <td>{item.UserName}</td>
//                           <td>{item.Date}</td>
//                           <td>{item.ProjectCode}</td>
//                           <td>{item.ProjectName || "N/A"}</td>
//                           <td>{item.TaskId}</td>
//                           <td>{item.TaskName || "N/A"}</td>
//                           <td>{item.Hours}</td>
//                           <td>{item.Remarks}</td>
//                           <td>{item.Location}</td>
//                           <td>
//                             {item.StatusName ||
//                               (item.Status === "W"
//                                 ? "Pending"
//                                 : item.Status === "A"
//                                   ? "Approved"
//                                   : item.Status === "R"
//                                     ? "Rejected"
//                                     : item.Status)}
//                           </td>
//                           <td className="tcr-actions-container">
//                             <Button
//                               variant="warning"
//                               size="sm"
//                               onClick={() => handleEditClick(item)}
//                               className="tcr-btn-warning-sm"
//                               title="Edit Timecard"
//                             >
//                               <i className="fas fa-edit"></i> Edit
//                             </Button>
//                             {isAdmin && (
//                               <Button
//                                 variant="danger"
//                                 size="sm"
//                                 onClick={() => handleDeleteClick(item)}
//                                 className="tcr-btn-danger-sm"
//                                 title="Delete Timecard"
//                               >
//                                 <i className="fas fa-trash"></i> Delete
//                               </Button>
//                             )}
//                           </td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </Table>
//                 ) : (
//                   <div className="tcr-empty-state">
//                     <p>No records found for the given criteria</p>
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Edit Timecard Modal - Updated with all editable fields */}
//       <Modal
//         show={showEditModal}
//         onHide={() => setShowEditModal(false)}
//         size="lg"
//       >
//         <Modal.Header closeButton className="tcr-modal-header">
//           <Modal.Title className="tcr-modal-title">
//             Edit Timecard - DTId: {selectedTimecard?.DTId}
//           </Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="tcr-modal-body">
//           <Form>
//             {/* Read-only User Info */}
//             <Row>
//               <Col md={6}>
//                 <Form.Group className="tcr-form-group">
//                   <Form.Label className="tcr-form-label">User Name</Form.Label>
//                   <Form.Control
//                     type="text"
//                     value={selectedTimecard?.UserName || ""}
//                     disabled
//                     readOnly
//                     className="tcr-form-control"
//                   />
//                 </Form.Group>
//               </Col>
//               <Col md={6}>
//                 <Form.Group className="tcr-form-group">
//                   <Form.Label className="tcr-form-label">Resource ID</Form.Label>
//                   <Form.Control
//                     type="text"
//                     value={selectedTimecard?.ResourceId || selectedTimecard?.ResourceCode || ""}
//                     disabled
//                     readOnly
//                     className="tcr-form-control"
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>

//             {/* Editable Date and Status */}
//             <Row>
//               <Col md={6}>
//                 <Form.Group className="tcr-form-group">
//                   <Form.Label className="tcr-form-label">Date *</Form.Label>
//                   <Form.Control
//                     type="date"
//                     value={editFormData.date}
//                     onChange={(e) =>
//                       setEditFormData({ ...editFormData, date: e.target.value })
//                     }
//                     className="tcr-form-control"
//                   />
//                 </Form.Group>
//               </Col>
//               <Col md={6}>
//                 <Form.Group className="tcr-form-group">
//                   <Form.Label className="tcr-form-label">Status</Form.Label>
//                   <Form.Select
//                     value={editFormData.status}
//                     onChange={(e) =>
//                       setEditFormData({ ...editFormData, status: e.target.value })
//                     }
//                     className="tcr-form-select"
//                   >
//                     <option value="W">Pending</option>
//                     <option value="A">Approved</option>
//                     <option value="R">Rejected</option>
//                   </Form.Select>
//                 </Form.Group>
//               </Col>
//             </Row>

//             {/* Editable Project */}
//             <Row>
//               <Col md={12}>
//                 <Form.Group className="tcr-form-group">
//                   <Form.Label className="tcr-form-label">Project *</Form.Label>
//                   <SearchableSelect
//                     value={editFormData.projectCode}
//                     onChange={handleEditProjectChange}
//                     options={editProjectOptions}
//                     placeholder="Select Project"
//                     disabled={fetchingEditProjects || editProjects.length === 0}
//                     loading={fetchingEditProjects}
//                     optionLabelKey="name"
//                     optionValueKey="id"
//                     searchPlaceholder="Search project..."
//                     noResultsText="No projects found"
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>

//             {/* Editable Task */}
//             <Row>
//               <Col md={12}>
//                 <Form.Group className="tcr-form-group">
//                   <Form.Label className="tcr-form-label">Task</Form.Label>
//                   <SearchableSelect
//                     value={editFormData.taskId}
//                     onChange={handleEditTaskChange}
//                     options={editTaskOptions}
//                     placeholder={
//                       !editFormData.projectCode
//                         ? "Select a project first"
//                         : fetchingEditTasks
//                           ? "Loading tasks..."
//                           : editTasks.length === 0
//                             ? "No tasks available"
//                             : "Select Task"
//                     }
//                     disabled={
//                       !editFormData.projectCode ||
//                       editTasks.length === 0 ||
//                       fetchingEditTasks
//                     }
//                     loading={fetchingEditTasks}
//                     optionLabelKey="name"
//                     optionValueKey="id"
//                     searchPlaceholder="Search task..."
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>

//             {/* Editable Hours, Location, Remarks */}
//             <Row>
//               <Col md={4}>
//                 <Form.Group className="tcr-form-group">
//                   <Form.Label className="tcr-form-label">Hours *</Form.Label>
//                   <Form.Control
//                     type="number"
//                     step="0.5"
//                     min="0"
//                     max="24"
//                     value={editFormData.hours}
//                     onChange={(e) =>
//                       setEditFormData({ ...editFormData, hours: e.target.value })
//                     }
//                     required
//                     className="tcr-form-control"
//                   />
//                 </Form.Group>
//               </Col>
//               <Col md={4}>
//                 <Form.Group className="tcr-form-group">
//                   <Form.Label className="tcr-form-label">Location</Form.Label>
//                   <Form.Control
//                     type="text"
//                     value={editFormData.location}
//                     onChange={(e) =>
//                       setEditFormData({ ...editFormData, location: e.target.value })
//                     }
//                     placeholder="Enter location"
//                     className="tcr-form-control"
//                   />
//                 </Form.Group>
//               </Col>
//               <Col md={4}>
//                 <Form.Group className="tcr-form-group">
//                   <Form.Label className="tcr-form-label">Resource ID</Form.Label>
//                   <Form.Control
//                     type="text"
//                     value={editFormData.resourceId}
//                     onChange={(e) =>
//                       setEditFormData({ ...editFormData, resourceId: e.target.value })
//                     }
//                     placeholder="Enter Resource ID"
//                     className="tcr-form-control"
//                   />
//                 </Form.Group>
//               </Col>
//             </Row>

//             {/* Editable Remarks */}
//             <Form.Group className="tcr-form-group">
//               <Form.Label className="tcr-form-label">Remarks</Form.Label>
//               <Form.Control
//                 as="textarea"
//                 rows={3}
//                 value={editFormData.remarks}
//                 onChange={(e) =>
//                   setEditFormData({ ...editFormData, remarks: e.target.value })
//                 }
//                 placeholder="Enter remarks"
//                 className="tcr-form-control"
//               />
//             </Form.Group>
//           </Form>
//         </Modal.Body>
//         <Modal.Footer className="tcr-modal-footer">
//           <Button
//             variant="secondary"
//             onClick={() => setShowEditModal(false)}
//             className="tcr-btn-secondary"
//           >
//             Cancel
//           </Button>
//           <Button
//             variant="primary"
//             onClick={handleUpdateTimecard}
//             disabled={updating}
//             className="tcr-btn-primary"
//           >
//             {updating ? "Saving..." : "Save Changes"}
//           </Button>
//         </Modal.Footer>
//       </Modal>

//       {/* Delete Confirmation Modal */}
//       <Modal
//         show={showDeleteConfirm}
//         onHide={() => setShowDeleteConfirm(false)}
//       >
//         <Modal.Header closeButton className="tcr-modal-header">
//           <Modal.Title className="tcr-modal-title">Confirm Delete</Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="tcr-modal-body">
//           <p>
//             Are you sure you want to delete timecard with DTId:{" "}
//             <strong>{timecardToDelete?.DTId}</strong>?
//           </p>
//           <p className="tcr-delete-warning">
//             This action can be undone by restoring from deleted items.
//           </p>
//           <hr />
//           <p>
//             <strong>Details:</strong>
//           </p>
//           <div className="tcr-delete-details">
//             <ul>
//               <li>User: {timecardToDelete?.UserName}</li>
//               <li>Date: {timecardToDelete?.Date}</li>
//               <li>
//                 Project: {timecardToDelete?.ProjectCode} -{" "}
//                 {timecardToDelete?.ProjectName}
//               </li>
//               <li>Hours: {timecardToDelete?.Hours}</li>
//             </ul>
//           </div>
//         </Modal.Body>
//         <Modal.Footer className="tcr-modal-footer">
//           <Button
//             variant="secondary"
//             onClick={() => setShowDeleteConfirm(false)}
//             className="tcr-btn-secondary"
//           >
//             Cancel
//           </Button>
//           <Button
//             variant="danger"
//             onClick={handleConfirmDelete}
//             disabled={updating}
//             className="tcr-btn-danger-sm"
//           >
//             {updating ? "Deleting..." : "Yes, Delete"}
//           </Button>
//         </Modal.Footer>
//       </Modal>

//       {/* Deleted Timecards Modal (Admin only) */}
//       <Modal
//         show={showDeletedModal}
//         onHide={() => setShowDeletedModal(false)}
//         size="xl"
//       >
//         <Modal.Header closeButton className="tcr-modal-header">
//           <Modal.Title className="tcr-modal-title">
//             Deleted Timecards
//           </Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="tcr-modal-body">
//           {deletedTimecards.length > 0 ? (
//             <div className="tcr-deleted-table-container">
//               <Table
//                 striped
//                 bordered
//                 hover
//                 size="sm"
//                 className="tcr-deleted-table"
//               >
//                 <thead>
//                   <tr>
//                     <th>DTId</th>
//                     <th>User Name</th>
//                     <th>Date</th>
//                     <th>Project Code</th>
//                     <th>Task ID</th>
//                     <th>Hours</th>
//                     <th>Remarks</th>
//                     <th>Deleted By</th>
//                     <th>Deleted On</th>
//                     <th>Actions</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {deletedTimecards.map((item) => (
//                     <tr key={item.dtid}>
//                       <td>{item.dtid}</td>
//                       <td>{item.userName}</td>
//                       <td>
//                         {item.date
//                           ? new Date(item.date).toLocaleDateString()
//                           : ""}
//                       </td>
//                       <td>{item.projectCode}</td>
//                       <td>{item.tasks}</td>
//                       <td>{item.hours}</td>
//                       <td>{item.remarks}</td>
//                       <td>{item.changedBy}</td>
//                       <td>
//                         {item.changedOnDate
//                           ? new Date(item.changedOnDate).toLocaleDateString()
//                           : ""}
//                       </td>
//                       <td>
//                         <Button
//                           variant="success"
//                           size="sm"
//                           onClick={() => handleRestoreTimecard(item.dtid)}
//                           disabled={updating}
//                           className="tcr-btn-restore-sm"
//                         >
//                           <i className="fas fa-trash-restore"></i> Restore
//                         </Button>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </Table>
//             </div>
//           ) : (
//             <p className="tcr-empty-state">No deleted timecards found</p>
//           )}
//         </Modal.Body>
//         <Modal.Footer className="tcr-modal-footer">
//           <Button
//             variant="secondary"
//             onClick={() => setShowDeletedModal(false)}
//             className="tcr-btn-secondary"
//           >
//             Close
//           </Button>
//         </Modal.Footer>
//       </Modal>

//       {(loading ||
//         fetchingResources ||
//         fetchingProjects ||
//         fetchingTasks ||
//         updating) && (
//         <div className="tcr-loading-overlay">
//           <Sample14 />
//         </div>
//       )}
//     </div>
//   );
// };

// export default TimeCardReport;
